import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { api, storePath } from "./api";

export type Me = { id: string; email: string; email_verified: boolean; name: string; locale: string; mfa_enabled: boolean };

export type ChooserStore = {
  id: string; handle: string; name: string; status: string; is_development: boolean;
  org_id?: string; org_name?: string; role: "owner" | "staff" | "collaborator"; plan?: string; last_opened_at?: string;
};

export type Org = { id: string; type: "merchant" | "partner"; name: string; role: string; partner_status?: string; referral_code?: string };

export type Plan = {
  id: string; key: string; name: string; interval: string; price_minor: number; currency: string;
  features: Record<string, boolean>; limits: Record<string, number>;
};
export type Subscription = { status: string; plan: Plan; trial_ends_at?: string; trial_days_left?: number; current_period_end: string; version: number };

export type Summary = {
  id: string; handle: string; name: string; status: string; is_development: boolean; country: string; currency: string;
  timezone: string; primary_locale: string; enabled_locales: string[]; storefront_url: string; launched: boolean;
  password_protected: boolean; brand: { primary_color?: string } | null; subscription: Subscription;
  me: { kind: string; owner: boolean; permissions: string[]; location_ids: string[]; support?: { session_id: string; operator: string; mode: "read" | "write"; expires_at: string } | null };
};

export const meQuery = { queryKey: ["me"], queryFn: () => api<Me>("/admin/v1/me"), staleTime: 60_000 };
export const orgsQuery = {
  queryKey: ["orgs"], queryFn: () => api<{ organizations: Org[] }>("/admin/v1/me/organizations").then((r) => r.organizations), staleTime: 60_000,
};

// useStore resolves the store in the URL (/store/$handle) to its id and summary.
export function useStore() {
  const { handle } = useParams({ strict: false }) as { handle?: string };
  const lookup = useQuery({
    queryKey: ["store-by-handle", handle],
    enabled: !!handle,
    staleTime: Infinity,
    queryFn: async () => {
      const r = await api<{ stores: ChooserStore[] }>(`/admin/v1/me/stores?q=${encodeURIComponent(handle!)}&limit=20`);
      const s = r.stores.find((x) => x.handle === handle);
      if (!s) throw new Error("store_not_found");
      return s;
    },
  });
  const id = lookup.data?.id;
  const summary = useQuery({
    queryKey: ["store", id, "summary"],
    enabled: !!id,
    queryFn: () => api<Summary>(storePath(id!, "/summary")),
  });
  return { handle: handle!, id, summary, lookup, can: (p: string) => !!summary.data?.me.permissions.includes(p) };
}

// useStoreApi: query keys are scoped by store id so switching stores never shows stale data.
export function useStoreQuery<T>(id: string | undefined, path: string, opts: { enabled?: boolean; refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: ["store", id, path],
    enabled: !!id && (opts.enabled ?? true),
    refetchInterval: opts.refetchInterval,
    queryFn: () => api<T>(storePath(id!, path)),
  });
}
